import React from 'react'
import {
    Typography,
    Paper,
    CircularProgress,
    Button,
    ButtonGroup,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TablePagination,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { Container } from '../../Container'
import {
    ProblemsSummary,
    ConversationProblemsResponse,
    ConversationProblem,
    ProblemFilters,
    ProblemCategory,
    PROBLEM_CATEGORY_OPTIONS,
} from './analysisTypes'
import { ProblemListParams } from '../../../hooks/useLoriConversationAnalysis'

const KPICard = ({
    title,
    value,
}: {
    title: string
    value: string | number
}) => {
    const theme = useTheme()
    return (
        <Paper
            style={{
                flex: 1,
                padding: theme.spacing(2),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 120,
            }}
            elevation={1}
        >
            <Typography variant="caption" color="textSecondary">
                {title}
            </Typography>
            <Typography variant="h4">{value}</Typography>
        </Paper>
    )
}

interface Props {
    summary: ProblemsSummary | null
    summaryLoading: boolean
    problemsList: ConversationProblemsResponse | null
    problemsListLoading: boolean
    problemFilters: ProblemFilters
    problemListParams: ProblemListParams
    onUpdateFilters: (partial: Partial<ProblemFilters>) => void
    onUpdateListParams: (partial: Partial<ProblemListParams>) => void
    selectedProblem: ConversationProblem | null
    onSelectProblem: (problem: ConversationProblem) => void
}

export const ProblemsView = ({
    summary,
    summaryLoading,
    problemsList,
    problemsListLoading,
    problemFilters,
    problemListParams,
    onUpdateFilters,
    onUpdateListParams,
    selectedProblem,
    onSelectProblem,
}: Props) => {
    const theme = useTheme()

    if (summaryLoading && !summary) {
        return (
            <Container
                style={{
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 400,
                }}
            >
                <CircularProgress />
            </Container>
        )
    }

    const categoryChartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            toolbar: { show: false },
        },
        plotOptions: {
            bar: { horizontal: true, barHeight: '60%' },
        },
        dataLabels: { enabled: true },
        xaxis: {
            categories: (summary?.by_category ?? []).map((c) => c.label),
        },
        colors: [theme.palette.error.main],
        tooltip: { enabled: true },
    }

    const categoryChartSeries = [
        {
            name: 'Problems',
            data: (summary?.by_category ?? []).map((c) => c.count),
        },
    ]

    const timelineOptions: ApexOptions = {
        chart: {
            type: 'area',
            toolbar: { show: false },
            zoom: { enabled: false },
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        xaxis: {
            categories: (summary?.over_time ?? []).map((p) => p.period),
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.4,
                opacityTo: 0.1,
            },
        },
        colors: [theme.palette.error.main],
        tooltip: { enabled: true },
    }

    const timelineSeries = [
        {
            name: 'Problems',
            data: (summary?.over_time ?? []).map((p) => p.count),
        },
    ]

    const page = problemListParams.page - 1
    const rowsPerPage = problemListParams.pageSize

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                gap: theme.spacing(2),
                padding: theme.spacing(2),
                overflowY: 'auto',
                minWidth: 0,
                minHeight: 0,
            }}
        >
            {/* KPI Row */}
            {summary && (
                <Container
                    style={{
                        flexDirection: 'row',
                        gap: theme.spacing(2),
                    }}
                >
                    <KPICard title="Total Problems" value={summary.total} />
                    {summary.by_category.slice(0, 3).map((cat) => (
                        <KPICard
                            key={cat.category}
                            title={cat.label}
                            value={cat.count}
                        />
                    ))}
                </Container>
            )}

            {/* Charts Row */}
            <Container
                style={{
                    flexDirection: 'row',
                    gap: theme.spacing(2),
                }}
            >
                {(summary?.by_category.length ?? 0) > 0 && (
                    <Paper
                        style={{
                            flex: 1,
                            padding: theme.spacing(2),
                            minWidth: 0,
                        }}
                        elevation={1}
                    >
                        <Typography variant="h6" gutterBottom>
                            By Category
                        </Typography>
                        <ReactApexChart
                            options={categoryChartOptions}
                            series={categoryChartSeries}
                            type="bar"
                            height={Math.max(
                                200,
                                (summary?.by_category.length ?? 0) * 35,
                            )}
                        />
                    </Paper>
                )}

                {(summary?.over_time.length ?? 0) > 0 && (
                    <Paper
                        style={{
                            flex: 1,
                            padding: theme.spacing(2),
                            minWidth: 0,
                        }}
                        elevation={1}
                    >
                        <Container
                            style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            <Typography variant="h6">
                                Problems Over Time
                            </Typography>
                            <ButtonGroup size="small" variant="outlined">
                                <Button
                                    variant={
                                        problemFilters.groupByTime === 'week'
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                    onClick={() =>
                                        onUpdateFilters({
                                            groupByTime: 'week',
                                        })
                                    }
                                >
                                    Week
                                </Button>
                                <Button
                                    variant={
                                        problemFilters.groupByTime === 'month'
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                    onClick={() =>
                                        onUpdateFilters({
                                            groupByTime: 'month',
                                        })
                                    }
                                >
                                    Month
                                </Button>
                            </ButtonGroup>
                        </Container>
                        <ReactApexChart
                            options={timelineOptions}
                            series={timelineSeries}
                            type="area"
                            height={250}
                        />
                    </Paper>
                )}
            </Container>

            {/* Filter + Table */}
            <Paper
                style={{ padding: theme.spacing(2), minWidth: 0 }}
                elevation={1}
            >
                <Container
                    style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: theme.spacing(2),
                    }}
                >
                    <Typography variant="h6">Flagged Problems</Typography>
                    <FormControl
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 180 }}
                    >
                        <InputLabel id="problem-filter-category-label">
                            Category
                        </InputLabel>
                        <Select
                            labelId="problem-filter-category-label"
                            value={problemFilters.category ?? ''}
                            onChange={(e) => {
                                const val = e.target.value as string
                                onUpdateFilters({
                                    category: val
                                        ? (val as ProblemCategory)
                                        : null,
                                })
                            }}
                            label="Category"
                        >
                            <MenuItem value="">All</MenuItem>
                            {PROBLEM_CATEGORY_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Container>

                {problemsListLoading && !problemsList ? (
                    <Container
                        style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 200,
                        }}
                    >
                        <CircularProgress />
                    </Container>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Category</TableCell>
                                        <TableCell>Workspace</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Conv. Date</TableCell>
                                        <TableCell>Flagged By</TableCell>
                                        <TableCell>Notes</TableCell>
                                        <TableCell>Flagged</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(problemsList?.results ?? []).map(
                                        (problem) => (
                                            <TableRow
                                                key={problem.id}
                                                hover
                                                selected={
                                                    selectedProblem?.id ===
                                                    problem.id
                                                }
                                                onClick={() =>
                                                    onSelectProblem(problem)
                                                }
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <TableCell>
                                                    <Chip
                                                        label={
                                                            problem.category_display
                                                        }
                                                        size="small"
                                                        style={{
                                                            backgroundColor:
                                                                theme.palette
                                                                    .error.main,
                                                            color:
                                                                theme.palette
                                                                    .error
                                                                    .contrastText,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        problem
                                                            .conversation_detail
                                                            ?.company?.name
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        problem
                                                            .conversation_detail
                                                            ?.user?.name
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    {problem.conversation_detail
                                                        ?.created_date
                                                        ? new Date(
                                                              problem.conversation_detail.created_date,
                                                          ).toLocaleDateString()
                                                        : ''}
                                                </TableCell>
                                                <TableCell>
                                                    {problem.flagged_by?.name}
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        maxWidth: 200,
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {problem.notes ?? ''}
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(
                                                        problem.created_date,
                                                    ).toLocaleDateString()}
                                                </TableCell>
                                            </TableRow>
                                        ),
                                    )}
                                    {(problemsList?.results ?? []).length ===
                                        0 && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={7}
                                                align="center"
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    No problems flagged
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {problemsList && problemsList.count > 0 && (
                            <TablePagination
                                component={'div' as React.ElementType}
                                count={problemsList.count}
                                page={page}
                                onChangePage={(_, newPage) =>
                                    onUpdateListParams({ page: newPage + 1 })
                                }
                                rowsPerPage={rowsPerPage}
                                onChangeRowsPerPage={(e) => {
                                    onUpdateListParams({
                                        pageSize: parseInt(e.target.value, 10),
                                        page: 1,
                                    })
                                }}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                            />
                        )}
                    </>
                )}
            </Paper>
        </Container>
    )
}
